import { useMemo } from 'react'

/**
 * Calculates the total price and total cost from a list of product lines.
 *
 * This hook aggregates the price and cost values by multiplying each line's
 * unit price and cost by its quantity. The cost is resolved using `Cost`,
 * falling back to `AvgCost` when `Cost` is not available.
 *
 * The calculation is memoized and will only be recalculated when the `lines`
 * array reference changes.
 *
 * @param {Array<Object>} lines - List of product line items.
 * @param {number} lines[].Price - Unit price of the product.
 * @param {number} lines[].Quantity - Quantity of the product.
 * @param {number} [lines[].Cost] - Unit cost of the product.
 * @param {number} [lines[].AvgCost] - Average unit cost used as fallback.
 *
 * @returns {{ totalPrice: number, totalCost: number }} An object containing:
 * - `totalPrice`: The aggregated total price.
 * - `totalCost`: The aggregated total cost.
 */
export const useTotalMargin = lines => {
  return useMemo(() => {
    // Guard clause: if lines is not an array, return zeros
    if (!Array.isArray(lines)) {
      return { totalPrice: 0, totalCost: 0 }
    }

    const validateCost = item => item.Cost || item.AvgCost || 0

    const totalPrice = lines.reduce(
      (acc, item) => acc + item.Price * item.Quantity,
      0
    )

    const totalCost = lines.reduce(
      (acc, item) => acc + validateCost(item) * item.Quantity,
      0
    )

    return { totalPrice, totalCost }
  }, [lines])
}
